import { useCallback, useEffect, useMemo, useState } from "react";

import { Button, Modal, ModalBody, ModalHeader } from "reactstrap";
import { XMarkIcon } from "@heroicons/react/24/outline";

import { apiSlice } from "app/apiSlice";
import ClipboardWriteButton from "components/ClipboardWriteButton";
import TabbedNav from "components/TabbedNav";
import TabPanes from "components/TabbedNav/TabPanes";
import { showToast } from "components/Toasts/helpers/showToast";
import { milestonesCardTabOptions } from "features/milestones/constants";

import "./index.scss";

/**
 * Renders a modal with the old form to create and manage (edit, pay, deliverables) a milestone
 * @deprecated This component is deprecated. Instead use `@features/milestones/components/CreateMilestoneModal`
 */
const MilestoneModal = ({
  milestone_id = "",
  assignment_id,
  isOpen,
  setIsOpen,
  defaultTabId = "view",
  onModalClosed = () => {}, // this is mostly needed for compatibility purposes, used for hacky fix to update stale data in old store after a possible rtk-query mutation
  ...props
}) => {
  const [activeTabId, setActiveTabId] = useState(
    defaultTabId || milestonesCardTabOptions[0]?.id,
  );
  const { milestone } = apiSlice.useGetAssignmentQuery(
    { _id: assignment_id },
    {
      selectFromResult: ({ data, error, isLoading }) => ({
        assignment: data,
        milestone:
          data?.milestones?.find(
            (milestone) => milestone._id === milestone_id,
          ) || {},
        error,
        isLoading,
      }),
      skip: !assignment_id,
      refetchOnMountOrArgChange: true,
    },
  );

  const [removeMilestone, { isLoading: isDeleting }]
    = apiSlice.useRemoveAssignmentMilestoneMutation();

  useEffect(() => {
    if (!milestone_id) {
      setActiveTabId(milestonesCardTabOptions[0]?.id);
    }
  }, [milestone_id]);

  const toggleModal = useCallback(() => {
    return setIsOpen((prevIsOpen) => !prevIsOpen);
  }, [setIsOpen]);

  const { canDelete, cantDeleteReasons } = useMemo(() => {
    let canDelete = true;
    const cantDeleteReasons = [];

    if (!milestone?._id) {
      return { canDelete: false, cantDeleteReasons: [] };
    }

    if (milestone.isComplete) {
      canDelete = false;
      cantDeleteReasons.push("Milestone is marked as complete.");
    }

    if (milestone.isPaid) {
      canDelete = false;
      cantDeleteReasons.push("Milestone has been paid.");
    }

    if (milestone.paidBalance && milestone.paidBalance > 0) {
      canDelete = false;
      cantDeleteReasons.push("Milestone has a paid balance.");
    }

    if (milestone.releasePayment) {
      canDelete = false;
      cantDeleteReasons.push("Payment has been released for this milestone.");
    }

    if (milestone.__deliverableMetrics?.total > 0) {
      canDelete = false;
      cantDeleteReasons.push("Milestone has deliverables.");
    }

    if (milestone.__agreementMetrics?.total > 0) {
      canDelete = false;
      cantDeleteReasons.push("Milestone has agreements.");
    }

    return { canDelete, cantDeleteReasons };
  }, [milestone]);

  const handleDelete = useCallback(async() => {
    if (!canDelete) {
      return;
    }

    if (!milestone?._id || !assignment_id) {
      showToast({
        type: "error",
        message: "Error: Missing milestone or assignment ID.",
      });
      return;
    }

    const confirmMessage = `Are you sure you want to delete the milestone "${milestone.name || milestone._id}"? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await removeMilestone({
        assignment_id,
        milestone_id: milestone._id,
      }).unwrap();
      showToast({
        type: "success",
        message: "Milestone deleted successfully.",
      });
      toggleModal();
    } catch (error) {
      console.error("Error deleting milestone:", error);
      showToast({
        type: "error",
        message: `Error deleting milestone. ${error?.data?.message || error?.message || "Unknown error"}`,
      });
    }
  }, [canDelete, milestone, assignment_id, removeMilestone, toggleModal]);

  if (milestone_id && !assignment_id) {
    console.error("No assignment_id provided to MilestoneModal component.");
    return null;
  }

  return (
    <div>
      <Modal
        size={activeTabId === "deliverables" ? "xl" : "md"}
        isOpen={isOpen}
        toggle={toggleModal}
        className="milestone-modal"
        backdrop={"static"}
        onClosed={onModalClosed}
      >
        <ModalHeader toggle={toggleModal}>
          <div>
            <span className="text-muted">
              {milestone_id ? "Manage Milestone" : "Create Milestone"}
            </span>
            {milestone?._id && <span className="ms-2">{milestone?.name}</span>}
          </div>
          {milestone?._id && (
            <div className="d-flex align-items-center mt-2">
              <span className="text-muted small me-2">ID:</span>
              <ClipboardWriteButton
                classNames="text-muted"
                textToCopy={milestone?._id}
              />
            </div>
          )}
        </ModalHeader>
        {milestone_id && (
          <ModalBody>
            <TabbedNav
              activeTabId={activeTabId}
              setActiveTabId={setActiveTabId}
              tabs={milestonesCardTabOptions}
            ></TabbedNav>
          </ModalBody>
        )}
        <TabPanes
          activeTabId={activeTabId}
          setActiveTabId={setActiveTabId}
          tabs={milestonesCardTabOptions}
          paneComponentProps={{
            milestone_id,
            assignment_id,
            toggleMilestoneModal: toggleModal,
          }}
        />
        {milestone_id && milestone?._id && (
          <ModalBody className="border-top">
            <div className="d-flex flex-column gap-2">
              <h6 className="text-danger mb-0">Danger Zone</h6>
              <Button
                color="danger"
                outline
                className="d-flex align-items-center align-self-start"
                disabled={!canDelete || isDeleting}
                onClick={handleDelete}
              >
                <XMarkIcon height={20} width={20} className="me-2" />
                {isDeleting ? "Deleting..." : "Delete Milestone"}
              </Button>
              {!canDelete && cantDeleteReasons.length > 0 && (
                <div className="text-danger small">
                  <div className="fw-bold mb-1">Cannot delete milestone:</div>
                  <ul className="mb-0 ps-3">
                    {cantDeleteReasons.map((reason, index) => (
                      <li key={index}>{reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </ModalBody>
        )}
      </Modal>
    </div>
  );
};

export default MilestoneModal;
